import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import ValueTrackingForm from '../components/ValueTrackingForm';
import ProcessSelector from '../components/ProcessSelector';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Separator } from '../components/ui/separator';
import { ArrowLeft, Save, Loader2, Sparkles, Upload, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import HelpTip from '../components/HelpTip';

const CreateIdea = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, isSubmitter } = useAuth();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [l2Processes, setL2Processes] = useState([]);
  const [l3Processes, setL3Processes] = useState([]);
  const [approvers, setApprovers] = useState([]);
  
  // AI categorization state
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState(null);

  // Form state
  const [title, setTitle] = useState('');
  const [problemStatement, setProblemStatement] = useState('');
  const [department, setDepartment] = useState('');
  const [processArea, setProcessArea] = useState('');
  const [location, setLocation] = useState('');
  const [ideaType, setIdeaType] = useState('Quick Win');
  const [description, setDescription] = useState('');
  const [approverId, setApproverId] = useState('');
  const [linkedProcessId, setLinkedProcessId] = useState('');
  const [linkedProcessLevel, setLinkedProcessLevel] = useState(null);
  
  // NEW: Financial and Operational Tracking
  const [financialData, setFinancialData] = useState({
    costs: { one_off_expected: 0, one_off_forecast: 0, ongoing_expected: 0, ongoing_forecast: 0 },
    savings: { one_off_expected: 0, ongoing_expected: 0 }
  });
  const [operationalMetrics, setOperationalMetrics] = useState([]);
  
  // Legacy value metrics (kept for backward compatibility)
  const [baselineMetricName, setBaselineMetricName] = useState('');
  const [baselineValue, setBaselineValue] = useState('');
  const [baselineUnit, setBaselineUnit] = useState('');
  const [expectedValue, setExpectedValue] = useState('');
  const [expectedUnit, setExpectedUnit] = useState('');
  
  // Effort & cost (priority scores)
  const [effortEstimate, setEffortEstimate] = useState('');
  const [complexity, setComplexity] = useState('');
  const [oneOffCost, setOneOffCost] = useState('');
  const [ongoingCost, setOngoingCost] = useState('');
  const [budgetCode, setBudgetCode] = useState('');
  const [impactScore, setImpactScore] = useState([3]);
  const [effortScore, setEffortScore] = useState([3]);
  const [notesForApprover, setNotesForApprover] = useState('');
  const [budgetApproved, setBudgetApproved] = useState(null);
  const [budgetAmount, setBudgetAmount] = useState('');

  useEffect(() => {
    fetchDropdownData();
    if (isEdit) {
      fetchIdea();
    } else {
      // Auto-link process from URL param
      const linkProc = searchParams.get('linkProcess');
      if (linkProc) setLinkedProcessId(linkProc);
    }
  }, [id]);

  // AI Auto-categorization
  const handleAiCategorize = async () => {
    if (!title || !problemStatement) {
      toast.error('Please enter a title and problem statement first');
      return;
    }
    
    setAiLoading(true);
    try {
      const response = await api.post('/ai/auto-categorize', {
        title,
        problem_statement: problemStatement,
        description: description || null
      });
      
      setAiSuggestions(response.data);
      toast.success('AI suggestions ready! Click to apply them.');
    } catch (error) {
      toast.error('AI categorization failed');
    } finally {
      setAiLoading(false);
    }
  };

  const applyAiSuggestions = () => {
    if (!aiSuggestions) return;
    
    if (aiSuggestions.suggested_type) setIdeaType(aiSuggestions.suggested_type);
    if (aiSuggestions.suggested_department) setDepartment(aiSuggestions.suggested_department);
    if (aiSuggestions.suggested_process_area) setProcessArea(aiSuggestions.suggested_process_area);
    if (aiSuggestions.suggested_effort) setEffortEstimate(aiSuggestions.suggested_effort);
    if (aiSuggestions.suggested_complexity) setComplexity(aiSuggestions.suggested_complexity);
    if (aiSuggestions.suggested_impact_score) setImpactScore([aiSuggestions.suggested_impact_score]);
    if (aiSuggestions.suggested_effort_score) setEffortScore([aiSuggestions.suggested_effort_score]);
    
    toast.success('AI suggestions applied!');
    setAiSuggestions(null);
  };

  const fetchDropdownData = async () => {
    try {
      const [deptRes, catRes, approverRes, procRes] = await Promise.all([
        api.get('/settings/departments'),
        api.get('/settings/categories'),
        api.get('/users/approvers'),
        api.get('/processes'),
      ]);
      setDepartments(deptRes.data);
      setCategories(catRes.data);
      setApprovers(approverRes.data);
      setL2Processes(procRes.data.filter(p => p.level === 2));
      setL3Processes(procRes.data.filter(p => p.level === 3));
    } catch (error) {
      console.error('Failed to fetch dropdown data');
    }
  };

  const fetchIdea = async () => {
    try {
      const response = await api.get(`/ideas/${id}`);
      const idea = response.data;
      
      setTitle(idea.title);
      setProblemStatement(idea.problem_statement);
      setDepartment(idea.department);
      setProcessArea(idea.process_area || '');
      setLocation(idea.location || '');
      setIdeaType(idea.idea_type);
      setDescription(idea.description || '');
      setApproverId(idea.approver_id || '');
      setLinkedProcessId(idea.linked_process_id || '');
      setLinkedProcessLevel(idea.linked_process_level || null);
      
      // Load new financial tracking data
      if (idea.financial_tracking) {
        setFinancialData(idea.financial_tracking);
      }
      if (idea.operational_metrics) {
        setOperationalMetrics(idea.operational_metrics);
      }
      
      // Legacy value metrics
      if (idea.value_metrics) {
        setBaselineMetricName(idea.value_metrics.baseline_metric_name || '');
        setBaselineValue(idea.value_metrics.baseline_value?.toString() || '');
        setBaselineUnit(idea.value_metrics.baseline_unit || '');
        setExpectedValue(idea.value_metrics.expected_value?.toString() || '');
        setExpectedUnit(idea.value_metrics.expected_unit || '');
      }
      
      if (idea.effort_cost) {
        setEffortEstimate(idea.effort_cost.effort_estimate || '');
        setComplexity(idea.effort_cost.complexity || '');
        setOneOffCost(idea.effort_cost.one_off_cost?.toString() || '');
        setOngoingCost(idea.effort_cost.ongoing_cost?.toString() || '');
        setBudgetCode(idea.effort_cost.budget_code || '');
        setImpactScore([idea.effort_cost.impact_score || 3]);
        setEffortScore([idea.effort_cost.effort_score || 3]);
        setNotesForApprover(idea.effort_cost.notes_for_approver || '');
      }
      setBudgetApproved(idea.budget_approved ?? null);
      setBudgetAmount(idea.budget_amount ? String(idea.budget_amount) : '');
    } catch (error) {
      toast.error('Failed to load idea');
      navigate('/ideas');
    } finally {
      setLoading(false);
    }
  };

  const calculatePriorityScore = () => {
    return impactScore[0] * effortScore[0];
  };

  const getPriorityLabel = () => {
    const impact = impactScore[0];
    const effort = effortScore[0];
    
    if (impact >= 4 && effort <= 2) return { label: 'Quick Win', color: 'text-emerald-600' };
    if (impact >= 4 && effort >= 4) return { label: 'Major Project', color: 'text-blue-600' };
    if (impact <= 2 && effort <= 2) return { label: 'Fill-In', color: 'text-amber-600' };
    return { label: 'Consider Carefully', color: 'text-slate-600' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !problemStatement || !department || !ideaType) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmitting(true);
    
    const ideaData = {
      title,
      problem_statement: problemStatement,
      department,
      process_area: processArea || null,
      location: location || null,
      idea_type: ideaType,
      description: description || null,
      approver_id: approverId || null,
      linked_process_id: linkedProcessId || null,
      linked_process_level: linkedProcessLevel || null,
      budget_approved: budgetApproved,
      budget_amount: budgetApproved && budgetAmount ? parseFloat(budgetAmount) : null,
      // NEW: Enhanced financial tracking
      financial_tracking: financialData,
      operational_metrics: operationalMetrics,
      // Legacy value_metrics (for backward compatibility)
      value_metrics: {
        baseline_metric_name: baselineMetricName || null,
        baseline_value: baselineValue ? parseFloat(baselineValue) : null,
        baseline_unit: baselineUnit || null,
        expected_value: expectedValue ? parseFloat(expectedValue) : null,
        expected_unit: expectedUnit || null,
      },
      effort_cost: {
        effort_estimate: effortEstimate || null,
        complexity: complexity || null,
        one_off_cost: financialData?.costs?.one_off_expected || (oneOffCost ? parseFloat(oneOffCost) : null),
        ongoing_cost: financialData?.costs?.ongoing_expected || (ongoingCost ? parseFloat(ongoingCost) : null),
        budget_code: budgetCode || null,
        impact_score: impactScore[0],
        effort_score: effortScore[0],
        priority_score: calculatePriorityScore(),
        notes_for_approver: notesForApprover || null,
      }
    };
    
    try {
      if (isEdit) {
        await api.put(`/ideas/${id}`, ideaData);
        toast.success('Idea updated successfully');
      } else {
        const response = await api.post('/ideas', ideaData);
        // Auto-submit for submitters
        if (isSubmitter()) {
          try { await api.post(`/ideas/${response.data.id}/submit`); } catch (e) { /* ok if fails */ }
          toast.success('Idea submitted!');
        } else {
          toast.success('Idea created successfully');
        }
        navigate(`/ideas/${response.data.id}`);
        return;
      }
      navigate(`/ideas/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save idea');
    } finally {
      setSubmitting(false);
    }
  };

  const processAreas = categories.filter(c => c.type === 'process_area');
  const locations = categories.filter(c => c.type === 'location');
  const priority = getPriorityLabel();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-[#235894]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="create-idea-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate(-1)}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">
            {isEdit ? 'Edit Idea' : 'Submit New Idea'}
          </h1>
          <p className="text-slate-500 mt-1">
            {isEdit ? 'Update your improvement idea' : 'Share your improvement idea with the CI team'}
          </p>
        </div>
        {!isEdit && (
          <Button
            type="button"
            variant="outline"
            onClick={handleAiCategorize}
            disabled={aiLoading || !title || !problemStatement}
            className="border-[#26B8A7] text-[#26B8A7] hover:bg-[#26B8A7]/10"
            data-testid="ai-categorize-btn"
          >
            {aiLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4 mr-2" />
            )}
            AI Auto-Categorize
          </Button>
        )}
      </div>

      {/* AI Suggestions Banner */}
      {aiSuggestions && (
        <Card className="border-2 border-[#26B8A7] bg-[#26B8A7]/5">
          <CardContent className="py-4">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-[#26B8A7] rounded-lg">
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900">AI Suggestions Ready</h3>
                <p className="text-sm text-slate-600 mt-1">{aiSuggestions.reasoning}</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className="px-2 py-1 bg-white rounded text-xs font-medium">
                    Type: {aiSuggestions.suggested_type}
                  </span>
                  {aiSuggestions.suggested_department && (
                    <span className="px-2 py-1 bg-white rounded text-xs font-medium">
                      Dept: {aiSuggestions.suggested_department}
                    </span>
                  )}
                  <span className="px-2 py-1 bg-white rounded text-xs font-medium">
                    Effort: {aiSuggestions.suggested_effort}
                  </span>
                  <span className="px-2 py-1 bg-white rounded text-xs font-medium">
                    Impact: {aiSuggestions.suggested_impact_score}/5
                  </span>
                  <span className="px-2 py-1 bg-slate-100 rounded text-xs text-slate-500">
                    Confidence: {aiSuggestions.confidence}
                  </span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  size="sm"
                  onClick={applyAiSuggestions}
                  className="bg-[#26B8A7] hover:bg-[#1fa393]"
                  data-testid="apply-ai-suggestions-btn"
                >
                  Apply Suggestions
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setAiSuggestions(null)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>Describe your improvement idea</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Title *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Give your idea a clear, concise title"
                    required
                    data-testid="idea-title-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="problem-statement">Problem Statement *</Label>
                  <Textarea
                    id="problem-statement"
                    value={problemStatement}
                    onChange={(e) => setProblemStatement(e.target.value)}
                    placeholder="Describe the problem you're trying to solve..."
                    rows={4}
                    required
                    data-testid="problem-statement-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="department">Department (Value Stream) * <HelpTip helpKey="ideas.department" /></Label>
                    <Select value={department} onValueChange={(v) => { setDepartment(v); setProcessArea(''); }} required>
                      <SelectTrigger data-testid="department-select">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                      <SelectContent>
                        {l2Processes.length > 0 ? (
                          l2Processes.map((p) => (
                            <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                          ))
                        ) : (
                          departments.map((dept) => (
                            <SelectItem key={dept.id} value={dept.name}>{dept.name}</SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="idea-type">Idea Type *</Label>
                    <Select value={ideaType} onValueChange={setIdeaType}>
                      <SelectTrigger data-testid="idea-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Quick Win">Quick Win</SelectItem>
                        <SelectItem value="Tech Project">Tech Project</SelectItem>
                        <SelectItem value="DMAIC">DMAIC</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="process-area">Process Area (E2E Process) <HelpTip helpKey="ideas.processArea" /></Label>
                    <Select value={processArea} onValueChange={setProcessArea}>
                      <SelectTrigger data-testid="process-area-select">
                        <SelectValue placeholder={department ? "Select process area" : "Select department first"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {(() => {
                          const selectedL2 = l2Processes.find(p => p.name === department);
                          const filtered = selectedL2
                            ? l3Processes.filter(p => p.parent_process_id === selectedL2.id)
                            : l3Processes;
                          return filtered.map((p) => (
                            <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>
                          ));
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="location">Location / Region</Label>
                    <Select value={location} onValueChange={setLocation}>
                      <SelectTrigger data-testid="location-select">
                        <SelectValue placeholder="Select location" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {locations.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Additional Details</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Any additional context or details..."
                    rows={3}
                    data-testid="description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="approver">Approver</Label>
                  <Select value={approverId} onValueChange={setApproverId}>
                    <SelectTrigger data-testid="approver-select">
                      <SelectValue placeholder="Select approver" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Auto-assign</SelectItem>
                      {approvers.map((approver) => (
                        <SelectItem key={approver.id} value={approver.id}>
                          {approver.name} ({approver.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Approver/Manager-only sections below */}
            {!isSubmitter() && (
            <>
            {/* Linked Process */}
            <Card>
              <CardContent className="pt-5">
                <ProcessSelector
                  value={linkedProcessId}
                  onChange={(id, level) => { setLinkedProcessId(id || ''); setLinkedProcessLevel(level); }}
                  label="Linked Process"
                  entityType="idea"
                />
              </CardContent>
            </Card>

            {/* NEW: Financial and Operational Value Tracking */}
            <ValueTrackingForm
              financialData={financialData}
              operationalMetrics={operationalMetrics}
              onChange={({ financial, operational }) => {
                setFinancialData(financial);
                setOperationalMetrics(operational);
              }}
              showForecast={true}
              showActuals={false}
            />

            {/* Effort & Priority Assessment */}
            <Card>
              <CardHeader>
                <CardTitle>Effort & Priority Assessment</CardTitle>
                <CardDescription>Help approvers understand the implementation effort</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Effort Estimate</Label>
                    <Select value={effortEstimate} onValueChange={setEffortEstimate}>
                      <SelectTrigger data-testid="effort-estimate-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Small">Small (1-3 days)</SelectItem>
                        <SelectItem value="Medium">Medium (1-2 weeks)</SelectItem>
                        <SelectItem value="Large">Large (3+ weeks)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Complexity</Label>
                    <Select value={complexity} onValueChange={setComplexity}>
                      <SelectTrigger data-testid="complexity-select">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Low">Low</SelectItem>
                        <SelectItem value="Medium">Medium</SelectItem>
                        <SelectItem value="High">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes for Approver</Label>
                  <Textarea
                    value={notesForApprover}
                    onChange={(e) => setNotesForApprover(e.target.value)}
                    placeholder="Any additional context for the approver..."
                    rows={2}
                    data-testid="notes-for-approver-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Budget */}
            <Card>
              <CardHeader>
                <CardTitle>Budget</CardTitle>
                <CardDescription>Budget approval status and cost codes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Budget Code</Label>
                    <Input
                      value={budgetCode}
                      onChange={(e) => setBudgetCode(e.target.value)}
                      placeholder="e.g., CC-001"
                      data-testid="budget-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Budget Approved?</Label>
                    <div className="flex gap-2 mt-1">
                      <Button type="button" size="sm" variant={budgetApproved === true ? 'default' : 'outline'} className={budgetApproved === true ? 'bg-emerald-600 hover:bg-emerald-700' : ''} onClick={() => setBudgetApproved(true)}>Yes</Button>
                      <Button type="button" size="sm" variant={budgetApproved === false ? 'default' : 'outline'} className={budgetApproved === false ? 'bg-amber-500 hover:bg-amber-600' : ''} onClick={() => setBudgetApproved(false)}>No</Button>
                      <Button type="button" size="sm" variant={budgetApproved === null ? 'default' : 'outline'} className="text-xs" onClick={() => setBudgetApproved(null)}>N/A</Button>
                    </div>
                  </div>
                </div>
                {budgetApproved === true && (
                  <div className="space-y-2">
                    <Label>Approved Budget Amount (£)</Label>
                    <Input
                      type="number"
                      value={budgetAmount}
                      onChange={(e) => setBudgetAmount(e.target.value)}
                      placeholder="e.g., 120000"
                      data-testid="budget-amount-input"
                    />
                    {budgetAmount && (
                      <p className="text-sm font-semibold text-emerald-700">£{parseFloat(budgetAmount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            </>
            )}
          </div>

          {/* Sidebar - Priority Assessment */}
          {!isSubmitter() && (
          <div className="space-y-6">
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle>Priority Assessment</CardTitle>
                <CardDescription>Score the impact and effort</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Impact Score</Label>
                      <span className="font-bold text-[#235894]">{impactScore[0]}/5</span>
                    </div>
                    <Slider
                      value={impactScore}
                      onValueChange={setImpactScore}
                      min={1}
                      max={5}
                      step={1}
                      className="[&_[role=slider]]:bg-[#235894]"
                      data-testid="impact-score-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>Low</span>
                      <span>High</span>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-2">
                      <Label>Effort Score</Label>
                      <span className="font-bold text-[#235894]">{effortScore[0]}/5</span>
                    </div>
                    <Slider
                      value={effortScore}
                      onValueChange={setEffortScore}
                      min={1}
                      max={5}
                      step={1}
                      className="[&_[role=slider]]:bg-[#235894]"
                      data-testid="effort-score-slider"
                    />
                    <div className="flex justify-between text-xs text-slate-400 mt-1">
                      <span>Low</span>
                      <span>High</span>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="text-center p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Priority Score</p>
                  <p className="text-3xl font-bold text-[#235894]">{calculatePriorityScore()}</p>
                  <p className={`text-sm font-medium mt-1 ${priority.color}`}>
                    {priority.label}
                  </p>
                </div>

                {/* Visual Matrix */}
                <div className="relative w-full aspect-square bg-slate-50 rounded-lg border border-slate-200 overflow-hidden">
                  <div className="absolute inset-0 grid grid-cols-2 grid-rows-2">
                    <div className="bg-blue-100/50 flex items-center justify-center">
                      <span className="text-xs text-blue-700 font-medium">Major</span>
                    </div>
                    <div className="bg-emerald-100/50 flex items-center justify-center">
                      <span className="text-xs text-emerald-700 font-medium">Quick Win</span>
                    </div>
                    <div className="bg-red-100/50 flex items-center justify-center">
                      <span className="text-xs text-red-700 font-medium">Low Priority</span>
                    </div>
                    <div className="bg-amber-100/50 flex items-center justify-center">
                      <span className="text-xs text-amber-700 font-medium">Fill-In</span>
                    </div>
                  </div>
                  <div 
                    className="absolute w-4 h-4 rounded-full bg-[#235894] border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2 z-10 transition-all duration-200"
                    style={{
                      left: `${(6 - effortScore[0]) / 5 * 100}%`,
                      top: `${(6 - impactScore[0]) / 5 * 100}%`,
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Save Button - Desktop Sidebar */}
            <Button
              type="submit"
              className="w-full bg-[#235894] hover:bg-[#1E4B80] hidden lg:flex"
              size="lg"
              disabled={submitting}
              data-testid="save-idea-btn-sidebar"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              <Save className="w-4 h-4 mr-2" />
              {isEdit ? 'Save Changes' : (isSubmitter() ? 'Submit Idea' : 'Save as Draft')}
            </Button>
          </div>
          )}
        </div>

        {/* Fixed Bottom Action Bar */}
        <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-white border-t border-slate-200 p-4 shadow-lg z-40">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
            <div className="hidden sm:block">
              <p className="text-sm text-slate-500">
                {title ? `Creating: ${title}` : 'Fill in the required fields to save your idea'}
              </p>
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate(-1)}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 sm:flex-none bg-[#235894] hover:bg-[#1E4B80] min-w-[160px]"
                size="lg"
                disabled={submitting}
                data-testid="save-idea-btn"
              >
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Save className="w-4 h-4 mr-2" />
                {isEdit ? 'Save Changes' : (isSubmitter() ? 'Submit Idea' : 'Save as Draft')}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Spacer for fixed bottom bar */}
        <div className="h-20" />
      </form>
    </div>
  );
};

export default CreateIdea;
